import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save, 
  Building2, 
  Phone, 
  Clock, 
  Share2, 
  FileText,
  ChevronLeft,
  LogOut,
  Settings,
  Package,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('company');
  
  const [settings, setSettings] = useState({
    // Company Info
    company_name_ar: '',
    company_name_en: '',
    company_name_de: '',
    logo_url: '',
    description_ar: '',
    description_en: '',
    description_de: '',
    
    // Contact Info
    phone: '',
    whatsapp: '',
    email: '',
    address_ar: '',
    address_en: '',
    address_de: '',
    google_maps_url: '',
    google_maps_embed: '',
    
    // Working Hours
    working_hours: {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
      saturday: '',
      sunday: '',
      note: ''
    },
    
    // Social Media
    social_media: {
      tiktok: '',
      instagram: '',
      facebook: '',
      twitter: '',
      youtube: ''
    },
    
    // Legal Info
    legal_info: {
      commercial_register: '',
      tax_number: '',
      license_number: '',
      vat_id: ''
    },
    
    // Copyright
    copyright_text_ar: '',
    copyright_text_en: '',
    copyright_text_de: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings`);
      if (res.ok) {
        const data = await res.json();
        setSettings({
          ...settings,
          ...data,
          working_hours: data.working_hours || settings.working_hours,
          social_media: data.social_media || settings.social_media,
          legal_info: data.legal_info || settings.legal_info
        });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('admin_token');
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'تم حفظ الإعدادات بنجاح!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const data = await res.json();
        setMessage({ type: 'error', text: data.detail || 'حدث خطأ أثناء الحفظ' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'خطأ في الاتصال بالخادم' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setSettings(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const tabs = [
    { id: 'company', label: 'معلومات الشركة', icon: Building2 },
    { id: 'contact', label: 'التواصل', icon: Phone },
    { id: 'hours', label: 'ساعات العمل', icon: Clock },
    { id: 'social', label: 'التواصل الاجتماعي', icon: Share2 },
    { id: 'legal', label: 'المعلومات القانونية', icon: FileText }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <div className="text-[#0d4023] text-xl">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5]" dir="rtl">
      {/* Header */}
      <header className="bg-[#0d4023] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="back-to-home"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-[#b89759]" />
                <h1 className="text-xl font-bold">إعدادات الموقع</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin/products')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="go-to-products"
              >
                <Package className="w-4 h-4" />
                <span>المنتجات</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span>خروج</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Message */}
        {message.text && (
          <div className={`mb-6 px-4 py-3 rounded-xl flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#0d4023] text-white'
                  : 'bg-white text-[#5a7062] hover:bg-[#0d4023]/5'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl border border-[#b89759]/10 p-6 lg:p-8">
          {/* Company Info Tab */}
          {activeTab === 'company' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#0d4023] mb-6">معلومات الشركة</h2>
              
              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">رابط الشعار</label>
                <input
                  type="url"
                  value={settings.logo_url}
                  onChange={(e) => updateField('logo_url', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  dir="ltr"
                  placeholder="https://..."
                  data-testid="input-logo"
                />
                {settings.logo_url && (
                  <div className="mt-3 w-24 h-24 rounded-xl overflow-hidden border border-[#b89759]/10">
                    <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain" />
                  </div>
                )}
              </div>

              {/* Company Names */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">اسم الشركة (عربي)</label>
                  <input
                    type="text"
                    value={settings.company_name_ar}
                    onChange={(e) => updateField('company_name_ar', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    data-testid="input-company-ar"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">اسم الشركة (إنجليزي)</label>
                  <input
                    type="text"
                    value={settings.company_name_en}
                    onChange={(e) => updateField('company_name_en', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    data-testid="input-company-en"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">اسم الشركة (ألماني)</label>
                  <input
                    type="text"
                    value={settings.company_name_de}
                    onChange={(e) => updateField('company_name_de', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    data-testid="input-company-de"
                  />
                </div>
              </div>

              {/* Descriptions */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">الوصف (عربي)</label>
                <textarea
                  value={settings.description_ar}
                  onChange={(e) => updateField('description_ar', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none resize-none"
                  data-testid="input-desc-ar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">الوصف (إنجليزي)</label>
                <textarea
                  value={settings.description_en}
                  onChange={(e) => updateField('description_en', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none resize-none"
                  dir="ltr"
                  data-testid="input-desc-en"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">الوصف (ألماني)</label>
                <textarea
                  value={settings.description_de}
                  onChange={(e) => updateField('description_de', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none resize-none"
                  dir="ltr"
                  data-testid="input-desc-de"
                />
              </div>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#0d4023] mb-6">معلومات التواصل</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">رقم الهاتف</label>
                  <input
                    type="tel"
                    value={settings.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="+49 123 456 789"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">رقم الواتساب</label>
                  <input
                    type="tel"
                    value={settings.whatsapp}
                    onChange={(e) => updateField('whatsapp', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="+49 123 456 789"
                    data-testid="input-whatsapp"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">البريد الإلكتروني</label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  dir="ltr"
                  placeholder="info@example.com"
                  data-testid="input-email"
                />
              </div>

              {/* Addresses */}
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">العنوان (عربي)</label>
                <input
                  type="text"
                  value={settings.address_ar}
                  onChange={(e) => updateField('address_ar', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  data-testid="input-address-ar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">العنوان (إنجليزي)</label>
                <input
                  type="text"
                  value={settings.address_en}
                  onChange={(e) => updateField('address_en', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  dir="ltr"
                  data-testid="input-address-en"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">العنوان (ألماني)</label>
                <input
                  type="text"
                  value={settings.address_de}
                  onChange={(e) => updateField('address_de', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  dir="ltr"
                  data-testid="input-address-de"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">رابط خرائط جوجل</label>
                <input
                  type="url"
                  value={settings.google_maps_url}
                  onChange={(e) => updateField('google_maps_url', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  dir="ltr"
                  placeholder="https://maps.google.com/..."
                  data-testid="input-maps-url"
                />
              </div>
            </div>
          )}

          {/* Working Hours Tab */}
          {activeTab === 'hours' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#0d4023] mb-6">ساعات العمل</h2>
              
              <div className="grid gap-4">
                {[
                  { key: 'monday', label: 'الإثنين' },
                  { key: 'tuesday', label: 'الثلاثاء' },
                  { key: 'wednesday', label: 'الأربعاء' },
                  { key: 'thursday', label: 'الخميس' },
                  { key: 'friday', label: 'الجمعة' },
                  { key: 'saturday', label: 'السبت' },
                  { key: 'sunday', label: 'الأحد' }
                ].map(day => (
                  <div key={day.key} className="flex items-center gap-4">
                    <label className="w-24 text-sm font-medium text-[#0d4023]">{day.label}</label>
                    <input
                      type="text"
                      value={settings.working_hours[day.key] || ''}
                      onChange={(e) => updateNestedField('working_hours', day.key, e.target.value)}
                      className="flex-1 px-4 py-2 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                      dir="ltr"
                      placeholder="06:00 - 20:00 أو مغلق"
                      data-testid={`input-hours-${day.key}`}
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0d4023] mb-2">ملاحظة إضافية</label>
                <input
                  type="text"
                  value={settings.working_hours.note || ''}
                  onChange={(e) => updateNestedField('working_hours', 'note', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                  placeholder="مثال: مفتوح في العطل الرسمية"
                  data-testid="input-hours-note"
                />
              </div>
            </div>
          )}

          {/* Social Media Tab */}
          {activeTab === 'social' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#0d4023] mb-6">وسائل التواصل الاجتماعي</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">TikTok</label>
                  <input
                    type="url"
                    value={settings.social_media.tiktok || ''}
                    onChange={(e) => updateNestedField('social_media', 'tiktok', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="https://tiktok.com/@username"
                    data-testid="input-social-tiktok"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">Instagram</label>
                  <input
                    type="url"
                    value={settings.social_media.instagram || ''}
                    onChange={(e) => updateNestedField('social_media', 'instagram', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="https://instagram.com/username"
                    data-testid="input-social-instagram"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">Facebook</label>
                  <input
                    type="url"
                    value={settings.social_media.facebook || ''}
                    onChange={(e) => updateNestedField('social_media', 'facebook', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="https://facebook.com/pagename"
                    data-testid="input-social-facebook"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">Twitter / X</label>
                  <input
                    type="url"
                    value={settings.social_media.twitter || ''}
                    onChange={(e) => updateNestedField('social_media', 'twitter', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="https://twitter.com/username"
                    data-testid="input-social-twitter"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">YouTube</label>
                  <input
                    type="url"
                    value={settings.social_media.youtube || ''}
                    onChange={(e) => updateNestedField('social_media', 'youtube', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="https://youtube.com/@channel"
                    data-testid="input-social-youtube"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Legal Info Tab */}
          {activeTab === 'legal' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#0d4023] mb-6">المعلومات القانونية</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">رقم السجل التجاري</label>
                  <input
                    type="text"
                    value={settings.legal_info.commercial_register || ''}
                    onChange={(e) => updateNestedField('legal_info', 'commercial_register', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="HRB 12345"
                    data-testid="input-legal-register"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">الرقم الضريبي</label>
                  <input
                    type="text"
                    value={settings.legal_info.tax_number || ''}
                    onChange={(e) => updateNestedField('legal_info', 'tax_number', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="12/345/67890"
                    data-testid="input-legal-tax"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">رقم الترخيص</label>
                  <input
                    type="text"
                    value={settings.legal_info.license_number || ''}
                    onChange={(e) => updateNestedField('legal_info', 'license_number', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    data-testid="input-legal-license"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">VAT ID</label>
                  <input
                    type="text"
                    value={settings.legal_info.vat_id || ''}
                    onChange={(e) => updateNestedField('legal_info', 'vat_id', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                    dir="ltr"
                    placeholder="DE123456789"
                    data-testid="input-legal-vat"
                  />
                </div>
              </div>

              <div className="border-t border-[#b89759]/10 pt-6 mt-6">
                <h3 className="text-lg font-bold text-[#0d4023] mb-4">نص حقوق النشر</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0d4023] mb-2">عربي</label>
                    <input
                      type="text"
                      value={settings.copyright_text_ar || ''}
                      onChange={(e) => updateField('copyright_text_ar', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                      placeholder="جميع الحقوق محفوظة"
                      data-testid="input-copyright-ar"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0d4023] mb-2">إنجليزي</label>
                    <input
                      type="text"
                      value={settings.copyright_text_en || ''}
                      onChange={(e) => updateField('copyright_text_en', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                      dir="ltr"
                      placeholder="All rights reserved"
                      data-testid="input-copyright-en"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0d4023] mb-2">ألماني</label>
                    <input
                      type="text"
                      value={settings.copyright_text_de || ''}
                      onChange={(e) => updateField('copyright_text_de', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none"
                      dir="ltr"
                      placeholder="Alle Rechte vorbehalten"
                      data-testid="input-copyright-de"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="mt-8 pt-6 border-t border-[#b89759]/10 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-8 py-3 bg-[#0d4023] text-white rounded-xl font-semibold hover:bg-[#1a5c32] transition-colors disabled:opacity-50"
              data-testid="save-settings-btn"
            >
              <Save className="w-5 h-5" />
              <span>{saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSettings;
